import React, { FunctionComponent } from 'react';
import { Link, useHistory, useLocation } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import Styles from 'components/leftNavBar/LeftNavBar.module.scss';
import backArrowIcon from 'assets/images/profile-icons/back-arrow.png';
import explainerVideoIcon from 'assets/images/profile-icons/explainer-video.png';
import inviteFriendIcon from 'assets/images/profile-icons/invite-friend.png';
import currencyIcon from 'assets/images/profile-icons/currency.png';
import subscriptionIcon from 'assets/images/profile-icons/subscription.png';
import editProfileIcon from 'assets/images/profile-icons/edit-profile.png';
import dateTimeIcon from 'assets/images/profile-icons/date-time.png';
import notificationsIcon from 'assets/images/profile-icons/notifications.png';
import feedbackIcon from 'assets/images/profile-icons/feedback.png';
import termsIcon from 'assets/images/profile-icons/terms.png';
import privacyIcon from 'assets/images/profile-icons/privacy.png';
import logoutIcon from 'assets/images/profile-icons/logout.png';
import { trackNavigation } from 'utils/clarity';
import { featureFlags } from 'config/featureFlags';
import { callLogout } from 'store/actions/auth.actions';

interface LeftNavBarProps {
  onLinkClick?: () => void;
}

interface NavItem {
  to: string;
  label: string;
  iconSrc: string;
}

const GENERAL_ITEMS: NavItem[] = [
  ...(featureFlags.showExplainerVideo ? [{ to: '/explainer-video', label: 'Explainer Video', iconSrc: explainerVideoIcon }] : []),
  { to: '/invite-friend', label: 'Invite a Friend', iconSrc: inviteFriendIcon },
];

const ACCOUNT_ITEMS: NavItem[] = [
  { to: '/currency', label: 'Donations', iconSrc: currencyIcon },
  { to: '/subscription', label: 'Super Host', iconSrc: subscriptionIcon },
  { to: '/profile/edit', label: 'Edit Profile', iconSrc: editProfileIcon },
  { to: '/date-and-time', label: 'Date and Time', iconSrc: dateTimeIcon },
  ...(featureFlags.showNotificationsSettings
    ? [{ to: '/notifications', label: 'Notifications', iconSrc: notificationsIcon }]
    : []),
  { to: '/email-notifications', label: 'Email Notifications', iconSrc: notificationsIcon },
];

const OTHER_ITEMS: NavItem[] = [
  { to: '/feedback', label: 'Send Us Feedback', iconSrc: feedbackIcon },
  { to: '/terms-and-conditions', label: 'Terms & Conditions', iconSrc: termsIcon },
  { to: '/privacy-policy', label: 'Privacy Policy', iconSrc: privacyIcon },
];

/** Phone top-bar title — same labels as the settings LeftNav. */
export function getSettingsPageTitle(pathname: string): string {
  const match = [...GENERAL_ITEMS, ...ACCOUNT_ITEMS, ...OTHER_ITEMS].find(
    (item) => item.to === pathname,
  );
  return match?.label ?? 'Settings';
}

const NavGroup: React.FC<{
  title: string;
  items: NavItem[];
  path: string;
  onLinkClick?: () => void;
  labelClassName?: string;
}> = ({ title, items, path, onLinkClick, labelClassName }) => (
  <>
    <li className={labelClassName || Styles.sectionLabel}>{title}</li>
    {items.map((item) => (
      <li key={item.to} className={path === item.to ? Styles.active : ''}>
        <Link
          to={item.to}
          className={Styles.navLink}
          onClick={() => {
            trackNavigation(item.to, "sidebar");
            if (onLinkClick) onLinkClick();
          }}
        >
          <span className={Styles.navIcon}>
            <img src={item.iconSrc} alt="" className={Styles.navIconImg} />
          </span>
          <span className={Styles.navText}>{item.label}</span>
        </Link>
      </li>
    ))}
  </>
);

const LeftNavBar: FunctionComponent<LeftNavBarProps> = ({ onLinkClick }) => {
  const location = useLocation();
  const path = location.pathname;
  const dispatch = useDispatch();
  const history = useHistory();

  const handleLogOut = () => {
    if (onLinkClick) onLinkClick();
    dispatch(callLogout());
    history.push('/login');
  };

  return (
    <nav className={Styles.navWrapper}>
      <Link
        to="/conversations-nearby"
        className={Styles.backLink}
        onClick={() => {
          trackNavigation("/conversations-nearby", "sidebar_back");
          if (onLinkClick) onLinkClick();
        }}
      >
        <img src={backArrowIcon} alt="" className={Styles.backArrowIcon} />
        <span>BACK TO DASHBOARD</span>
      </Link>

      <ul className={Styles.listingWrapper}>
        <NavGroup
          title="GENERAL"
          items={GENERAL_ITEMS}
          path={path}
          onLinkClick={onLinkClick}
          labelClassName={Styles.sectionLabelHighlight}
        />
        <NavGroup
          title="ACCOUNT"
          items={ACCOUNT_ITEMS}
          path={path}
          onLinkClick={onLinkClick}
          labelClassName={Styles.sectionLabelHighlight}
        />
        <NavGroup
          title="OTHER"
          items={OTHER_ITEMS}
          path={path}
          onLinkClick={onLinkClick}
          labelClassName={Styles.sectionLabelHighlight}
        />
      </ul>

      <div className={Styles.sidebarFooter}>
        <button type="button" className={Styles.logoutBtn} onClick={handleLogOut}>
          <span className={Styles.navIcon}>
            <img src={logoutIcon} alt="" className={Styles.navIconImg} />
          </span>
          <span className={Styles.navText}>Logout</span>
        </button>
      </div>
    </nav>
  );
};

export default LeftNavBar;
